import { css, cx } from '@emotion/css';
import { useEffect, useState } from 'react';
import type { HTMLAttributes } from 'react';
import { FiCalendar, FiClock, FiMapPin, FiDownload, FiEye, FiTag, FiHash } from 'react-icons/fi';
import { useGetTicketImage, useGetUserTickets } from '../../hooks';
import { eventsService } from '../../../../api/services/events';
import { useQuery } from '@tanstack/react-query';
import TicketDetailsModal from '../profile-ticket-details-modal/profile-ticket-details-modal';
import { formatTrustedDate, formatTrustedTime } from '../../../../shared/utils/datetime';

// Section styles consistent with profile modules
const container = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  padding: 'var(--spacing-lg)',
  boxShadow: 'var(--shadow-200)',
  '@media (max-width: 480px)': { padding: 'var(--spacing-md)' },
  '@media (max-width: 360px)': { padding: 'var(--spacing-sm)' },
});

const header = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-lg)',
});

const title = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 0,
});

const body = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
});

const subheader = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--spacing-md)',
  marginBottom: 'var(--spacing-md)',
  color: 'var(--g-600)',
});

const list = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
});

const card = css({
  backgroundColor: 'var(--primary-color)',
  border: '1px solid var(--g-200)',
  borderRadius: 'var(--spacing-xs)',
  overflow: 'hidden',
  boxShadow: 'var(--shadow-100)',
});

const cardMain = css({
  padding: 'var(--spacing-md)',
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 'var(--spacing-md)',
  cursor: 'pointer',
  '@media (max-width: 480px)': {
    gridTemplateColumns: '1fr',
  },
});

const cardHeader = css({
  marginBottom: 'var(--spacing-sm)',
});

const eventName = css({
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 'var(--spacing-xs)',
});

const meta = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--spacing-sm)',
});

const metaItem = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-3xs)',
  fontSize: 'var(--fs-350)',
  color: 'var(--g-600)',
});

const details = css({
  display: 'flex',
  gap: 'var(--spacing-lg)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    gap: 'var(--spacing-sm)',
  },
});

const detailCol = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
});

const label = css({
  fontSize: 'var(--fs-300)',
  color: 'var(--g-500)',
});

const value = css({
  fontSize: 'var(--fs-350)',
  fontWeight: 500,
  color: 'var(--g-800)',
});

const actions = css({
  display: 'flex',
  borderTop: '1px solid var(--g-100)',
});

const actionBtn = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--spacing-2xs)',
  padding: 'var(--spacing-sm)',
  background: 'none',
  border: 'none',
  fontSize: 'var(--fs-350)',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  color: 'var(--pc-600)',
  '&:hover': { backgroundColor: 'var(--g-100)' },
  '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
});

const actionDivider = css({ borderRight: '1px solid var(--g-100)' });

const footer = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 'var(--spacing-md)',
});

const pagination = css({
  display: 'flex',
  gap: 'var(--spacing-xs)',
  alignItems: 'center',
});

const pageBtn = css({
  padding: 'var(--spacing-3xs) var(--spacing-xs)',
  borderRadius: 'var(--spacing-3xs)',
  border: '1px solid var(--g-200)',
  background: 'var(--primary-color)',
  cursor: 'pointer',
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

export interface ProfileTicketsScheduleProps extends HTMLAttributes<HTMLDivElement> {
  scheduleId: string;
}

function useScheduleDetail(scheduleId?: string) {
  return useQuery({
    queryKey: ['schedule-detail', scheduleId],
    queryFn: async () => {
      if (!scheduleId) return null;
      const { schedule } = await eventsService.getScheduleById(scheduleId);
      return schedule;
    },
    enabled: !!scheduleId,
  });
}

export function ProfileTicketsSchedule({ scheduleId, className, style, ...props }: ProfileTicketsScheduleProps) {
  const [page, setPage] = useState(1);
  const limit = 10;

  // State to manage fetching image for download
  const [imageTicketId, setImageTicketId] = useState<string | null>(null);
  const [imageFilename, setImageFilename] = useState<string>('ingresso.jpg');
  const { data: imageBlob, isFetching: isDownloadingImage } = useGetTicketImage(imageTicketId ?? undefined, !!imageTicketId);

  // When blob is ready, trigger a browser download and cleanup
  useEffect(() => {
    if (imageBlob && imageTicketId) {
      const url = URL.createObjectURL(imageBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = imageFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // reset state
      setImageTicketId(null);
    }
  }, [imageBlob, imageTicketId, imageFilename]);

  // State to manage Ticket Details modal
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const { data: schedule } = useScheduleDetail(scheduleId);
  const { data: ticketsResp, isLoading, isError } = useGetUserTickets({ scheduleId, page, limit });

  const items = ticketsResp?.items || [];
  const totalPages = ticketsResp?.totalPages || 1;

  const locationName = schedule?.location?.name || 'Local a definir';
  const eventDateFmt = schedule?.date ? formatTrustedDate(schedule.date) : 'Data a definir';
  const eventTimeFmt = schedule?.date ? formatTrustedTime(schedule.date) : '—';

  const onPrev = () => setPage((p) => Math.max(1, p - 1));
  const onNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <section className={cx(container, className)} style={style} {...props}>
      <div className={header}>
        <h2 className={title}>Ingressos do evento</h2>
      </div>
      <div className={body}>
        <div className={subheader}>
          <div className={metaItem}>
            <FiCalendar size={16} />
            <span>{eventDateFmt}</span>
          </div>
          <div className={metaItem}>
            <FiClock size={16} />
            <span>{eventTimeFmt}</span>
          </div>
          <div className={metaItem}>
            <FiMapPin size={16} />
            <span>{locationName}</span>
          </div>
        </div>

        {isLoading && <p>Carregando ingressos...</p>}
        {isError && <p>Não foi possível carregar os ingressos.</p>}

        {!isLoading && !isError && (
          <>
            {items.length === 0 ? (
              <p>Nenhum ingresso encontrado para este horário.</p>
            ) : (
              <div className={list}>
                {items.map((t) => {
                  const zone = t.seatZone || 'Não especificada';
                  const seat = (t.seatRow || t.seatColumn)
                    ? `${t.seatRow || ''}${t.seatColumn || ''}`.trim()
                    : 'Não especificado';
                  const issued = t.issuedAt ? formatTrustedDate(t.issuedAt) : '—';
                  const statusLabel = (t.status || 'issued').toString();
                  const isThisDownloading = isDownloadingImage && imageTicketId === t.id;
                  return (
                    <div key={t.id} className={card}>
                      <div
                        className={cardMain}
                        onClick={() => {
                          setSelectedTicket(t);
                          setIsDetailsOpen(true);
                        }}
                      >
                        <div>
                          <div className={cardHeader}>
                            <h3 className={eventName}>Ingresso #{t.code || t.id.substring(0,8)}</h3>
                            <div className={meta}>
                              <div className={metaItem}><FiTag size={16} /><span>Status: {statusLabel}</span></div>
                              <div className={metaItem}><FiHash size={16} /><span>Pedido: {t.orderId?.substring(0,8) || '—'}</span></div>
                            </div>
                          </div>
                          <div className={details}>
                            <div className={detailCol}>
                              <span className={label}>Zona</span>
                              <span className={value}>{zone}</span>
                            </div>
                            <div className={detailCol}>
                              <span className={label}>Assento</span>
                              <span className={value}>{seat}</span>
                            </div>
                            <div className={detailCol}>
                              <span className={label}>Tipo</span>
                              <span className={value}>{t.ticketType || 'Inteira'}</span>
                            </div>
                            <div className={detailCol}>
                              <span className={label}>Emitido em</span>
                              <span className={value}>{issued}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className={actions}>
                        <button
                          className={cx(actionBtn, actionDivider)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageFilename(`ingresso-${t.code || t.id.substring(0, 8)}.jpg`);
                            setImageTicketId(t.id);
                          }}
                          disabled={isThisDownloading}
                        >
                          <FiDownload size={16} />
                          <span>{isThisDownloading ? 'Baixando...' : 'Baixar ingresso'}</span>
                        </button>
                        <button
                          className={actionBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTicket(t);
                            setIsDetailsOpen(true);
                          }}
                        >
                          <FiEye size={16} />
                          <span>Ver detalhes</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className={footer}>
                <div />
                <div className={pagination}>
                  <button className={pageBtn} onClick={onPrev} disabled={page <= 1}>Anterior</button>
                  <span>Página {page} de {totalPages}</span>
                  <button className={pageBtn} onClick={onNext} disabled={page >= totalPages}>Próxima</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <TicketDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
        eventTitle={schedule?.event?.title}
        locationName={schedule?.location?.name}
        eventDate={schedule?.date}
      />
    </section>
  );
}

export default ProfileTicketsSchedule;
